import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { REPORTS_PAGE_MIN_DATE } from "constants/constants";
import { useTranslation } from "react-i18next";
import type { ShiftSegment, TimeRangeFinite } from "types/types";
import { useShifts, useVisibleGroups } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  ReportItemProps,
  CustomReportWithSchedule,
  ReportScheduleType,
  MultiDayReportConfigType,
} from "./reportPageTypes";
import {
  useReportConfigTimeZone,
  getAndSortCompletedShifts,
  getDailyReportTimes,
  getWeeklyReportTimes,
} from "./reportPageUtils";
import ReportItemAccordion from "./ReportItemAccordion";
import ReportItems from "./ReportItems";
import { Shift } from "reports/types";
import { AMERICA_NEW_YORK } from "utils/timeUtils";

/**
 * Get list of reports to be shown on ReportsPage
 *
 * @param {{
 *   customReportItems?: ReportItemProps[];
 *   customReportItemsWithSchedule?: CustomReportWithSchedule[];
 * }} {
 *   customReportItems,
 *   customReportItemsWithSchedule
 * }
 * @return
 */

export const useReportsPropsList = ({
  customReportItems,
  customReportItemsWithSchedule,
  calendarSelectedDay,
}: {
  customReportItems?: ReportItemProps[];
  customReportItemsWithSchedule?: CustomReportWithSchedule[];
  calendarSelectedDay: ShiftSegment | null;
}): Omit<ReportItemProps, "initialExpandState">[] => {
  /**
   * If the customer was just deployed, and we have yet to configure the reports we use this date
   * this is so that they cannot go to far and cause a crash
   * In the vaste majority of cases (if not all), they should be configured and going back should be ok
   */
  const shifts = useShifts();
  const { i18n } = useTranslation("translation");

  const { translate } = useFlightTranslation();
  const reportConfig = useReportConfig();

  const timeZoneConfig = useReportConfigTimeZone();
  const timeZone = timeZoneConfig ?? AMERICA_NEW_YORK;
  const processGroupNames = useVisibleGroups().map(group => group.shortName);
  /** if the top-level config doesn't specify machineGroups or specifies [], use all groups */
  const machineGroups =
    reportConfig?.machineGroups && reportConfig.machineGroups.length
      ? reportConfig.machineGroups
      : processGroupNames;
  const eosReportTime = reportConfig?.startTime ?? REPORTS_PAGE_MIN_DATE;
  const shouldSkipEOS =
    !eosReportTime || (reportConfig?.shouldSkipEOS ?? false);
  const shouldGroupByMachineGroup =
    reportConfig?.shouldGroupByMachineGroup ?? false;
  const dailyReportConfig = reportConfig?.dailyReportConfig;
  const weeklyReportConfig = reportConfig?.weeklyReportConfig;
  const weeklyPerformanceReportConfig =
    reportConfig?.performanceReportConfig?.weekly;
  const shouldSkipNPS = reportConfig?.shouldSkipNPS;

  const sortedShifts = getAndSortCompletedShifts({
    shifts,
    startDateTimeToShowReports:
      calendarSelectedDay?.range.ge ?? eosReportTime ?? 0,
    shouldSkipNPS,
  });

  const lastShiftLt = sortedShifts?.[sortedShifts?.length - 1]?.range.lt;
  const dailyReportTimeRanges =
    (dailyReportConfig?.machineGroups &&
      dailyReportConfig.machineGroups.length) ||
    (dailyReportConfig && !dailyReportConfig?.machineGroups)
      ? getDailyReportTimes({
          dailyReportTimes: dailyReportConfig,
          timeZone,
          lastShiftLt,
        })
      : [];

  const weeklyReportTimeRanges = weeklyReportConfig
    ? getWeeklyReportTimes({
        weeklyReportTimes: weeklyReportConfig,
        timeZone,
        calendarSelectedDay,
      })
    : [];

  const weeklyPerformanceReportTimeRanges = getWeeklyReportTimes({
    weeklyReportTimes: weeklyPerformanceReportConfig,
    timeZone,
    calendarSelectedDay,
  });
  const custom = customReportItems ? customReportItems : [];
  const withSchedules = customReportItemsWithSchedule
    ? customReportItemsWithSchedule.map(customReportItemWithSchedule =>
        getCustomerReportItemWithScheduleProps({
          customReportItemWithSchedule,
          sortedShifts,
          dailyReportTimeRanges,
          weeklyReportTimeRanges,
          i18n,
          timeZone,
          shouldGroupByMachineGroup,
        })
      )
    : [];
  const eos = !shouldSkipEOS
    ? [
        getReportProps({
          reportTitle: translate("End_of_Shift"),
          sortedShifts,
          reportGroups: machineGroups,
          i18n,
          shouldGroupByMachineGroup,
          reportScheduleType: ReportScheduleType.SHIFTLY,
        }),
      ]
    : [];

  const daily = dailyReportConfig
    ? [
        getReportProps({
          reportTitle: dailyReportConfig.reportTitle ?? translate("24_Hour"),
          timeRanges: dailyReportTimeRanges,
          reportGroups: dailyReportConfig.machineGroups ?? machineGroups,
          shouldGroupByMachineGroup,
          reportScheduleType: ReportScheduleType.DAILY,
        }),
      ]
    : [];
  const weekly = weeklyReportConfig
    ? [
        getReportProps({
          reportTitle: weeklyReportConfig.reportTitle ?? translate("Weekly"),
          timeRanges: weeklyReportTimeRanges,
          reportGroups: weeklyReportConfig.machineGroups ?? machineGroups,
          shouldGroupByMachineGroup,
          reportScheduleType: ReportScheduleType.WEEKLY,
        }),
      ]
    : [];
  const weeklyPerformance = weeklyPerformanceReportConfig
    ? [
        getReportProps({
          reportTitle: "Weekly Performance",
          timeRanges: weeklyPerformanceReportTimeRanges,
          reportPath: "performance/weekly",
          reportGroups: weeklyPerformanceReportConfig.machineGroups
            ? weeklyPerformanceReportConfig.machineGroups
            : machineGroups,
          shouldGroupByMachineGroup,
          reportScheduleType: ReportScheduleType.WEEKLY,
        }),
      ]
    : [];
  const reports = [
    ...custom,
    ...withSchedules,
    ...eos,
    ...daily,
    ...weekly,
    ...weeklyPerformance,
  ].map(report => ({
    ...report,
    reportTitle: decodeURIComponent(report.reportTitle),
  }));
  return reports;
};

/**
 * Get ReportItem (or ReportItems if nested list)
 *
 * @param {{
 *   reportTitle: string;
 *   reportType: ReportScheduleType;
 *   shouldGroupByMachineGroup: boolean;
 *   sortedShifts?: ShiftSegment[];
 *   timeRanges?: TimeRangeFinite[];
 *   reportGroups?: string[];
 *   reportPath?: string;
 *   i18n?: any;
 * }} {
 *   reportTitle,
 *   sortedShifts,
 *   timeRanges,
 *   reportGroups,
 *   reportPath = "eos",
 *   reportType,
 *   i18n,
 *   shouldGroupByMachineGroup,
 * }
 * @return {*}
 */
const getReportProps = ({
  reportTitle,
  sortedShifts,
  timeRanges,
  reportGroups,
  reportPath = "eos",
  reportScheduleType,
  i18n,
  shouldGroupByMachineGroup,
}: {
  reportTitle: string;
  reportScheduleType: ReportScheduleType;
  shouldGroupByMachineGroup: boolean;
  sortedShifts?: ShiftSegment[];
  timeRanges?: TimeRangeFinite[];
  reportGroups?: string[];
  reportPath?: string;
  i18n?: any;
}) => {
  const reportItemsProps = {
    reportPath,
    reportGroups,
    shouldGroupByMachineGroup,
    reportScheduleType,
  };

  // EOS (shiftly) reports
  const reportItemByShift = (machineGroup?: string) =>
    sortedShifts?.map(({ shift, range }) => {
      const shiftName =
        i18n && i18n.isInitialized && i18n.language.slice(0, 2) === "en"
          ? getShiftName(shift)
          : shift.name;
      return (
        <ReportItems
          key={range.ge}
          {...{
            ...reportItemsProps,
            range,
            reportSubPath: machineGroup,
            name: shiftName ?? "",
          }}
        />
      );
    });

  // Multiday reports
  const reportItemsByTimeRange = (machineGroup?: string) =>
    timeRanges?.map(range => {
      return (
        <ReportItems
          key={range.ge}
          {...{
            ...reportItemsProps,
            range,
            reportSubPath: machineGroup,
            name: reportTitle,
          }}
        />
      );
    });

  const getReportContent = (machineGroup?: string) =>
    (reportScheduleType as ReportScheduleType) === ReportScheduleType.SHIFTLY
      ? reportItemByShift(machineGroup)
      : reportItemsByTimeRange(machineGroup);
  const shouldGroup = shouldGroupByMachineGroup && reportGroups;

  const reportContent = shouldGroup
    ? reportGroups.map(machineGroup => {
        return (
          <ReportItemAccordion
            key={machineGroup}
            initialExpandState={false}
            reportTitle={machineGroup}
            isInsideExpand={true}
            reportContent={getReportContent(machineGroup)}
          />
        );
      })
    : getReportContent();

  return {
    reportTitle,
    reportContent,
  };
};

/**
 * Gets proper shift name with Shift as a prefix or suffix if needed
 *
 * @param {Shift} shift
 */
const getShiftName = (shift: Shift) =>
  shift.name?.includes("Shift") // If it's a shift report and shift name already contains "Shift", don't add "Shift"
    ? shift.name
    : /^-?\d+$/.test(shift.name || "") // If shift name is a number only
      ? `Shift ${shift.name}` // prefix with Shift
      : `${shift.name} Shift`; // else suffix

/**
 * Get report props based on EOS/daily/weekly schedule for a custom report link
 *
 * @param {{
 *   customReportItemWithSchedule: CustomReportWithSchedule;
 *   sortedShifts: ShiftSegment[];
 *   dailyReportTimeRanges: TimeRangeFinite[];
 *   weeklyReportTimeRanges: TimeRangeFinite[];
 * }} {
 *   customReportItemWithSchedule,
 *   sortedShifts,
 *   dailyReportTimeRanges,
 *   weeklyReportTimeRanges
 * }
 * @return {*}
 */
const getCustomerReportItemWithScheduleProps = ({
  customReportItemWithSchedule,
  sortedShifts,
  dailyReportTimeRanges,
  weeklyReportTimeRanges,
  i18n,
  timeZone,
  shouldGroupByMachineGroup,
}: {
  customReportItemWithSchedule: CustomReportWithSchedule;
  sortedShifts: ShiftSegment[];
  dailyReportTimeRanges: TimeRangeFinite[];
  weeklyReportTimeRanges: TimeRangeFinite[];
  i18n: any;
  timeZone: string;
  shouldGroupByMachineGroup: boolean;
}) => {
  const { reportScheduleType, multiDayReportConfig } =
    customReportItemWithSchedule;

  const multiDayTimeRanges = multiDayReportConfig
    ? getMultiDayTimeRanges({
        multiDayReportConfig,
        timeZone,
      })
    : undefined;

  return getReportProps({
    ...customReportItemWithSchedule,
    shouldGroupByMachineGroup,
    sortedShifts,
    i18n,
    timeRanges:
      (multiDayTimeRanges ??
      (reportScheduleType as ReportScheduleType) === ReportScheduleType.DAILY)
        ? dailyReportTimeRanges
        : weeklyReportTimeRanges,
  });
};
const getMultiDayTimeRanges = ({
  multiDayReportConfig,
  timeZone,
}: {
  multiDayReportConfig: MultiDayReportConfigType[];
  timeZone: string;
}) => {
  return multiDayReportConfig
    .map(config => {
      return getWeeklyReportTimes({
        weeklyReportTimes: config,
        timeZone,
      });
    })
    .reduce((prev, next) => {
      return prev.concat(next);
    })
    .sort((a, b) => b.ge - a.ge);
};
